import { Rotate3D } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

import { type LabSceneFactory, readLabPalette } from './labScene3d';

const MAX_FRAME_S = 0.05;

/**
 * Host for a Chemistry 3D lab scene. It owns WebGL setup, the no-WebGL text fallback, resizing,
 * the animation clock (stopped by `playing` or reduced motion) and disposal. The scene factory is
 * created once; later `state` changes reach it through `update`, so inputs never rebuild the scene.
 */
export function LabCanvas3D<S>({
  build,
  state,
  playing,
  label,
  hint = 'Drag to turn the view',
  heightClass = 'h-[340px] sm:h-[400px]',
}: {
  build: LabSceneFactory<S>;
  state: S;
  playing: boolean;
  label: string;
  hint?: string;
  /** Canvas height classes; tall apparatus such as a burette needs a taller canvas. */
  heightClass?: string;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef(state);
  const playingRef = useRef(playing);
  const buildRef = useRef(build);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true });
    } catch {
      setUnavailable(true);
      return undefined;
    }
    setUnavailable(false);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
    renderer.domElement.setAttribute('aria-hidden', 'true');
    Object.assign(renderer.domElement.style, { position: 'absolute', inset: '0', display: 'block', width: '100%', height: '100%', touchAction: 'pan-y' });
    stage.appendChild(renderer.domElement);

    const reducedMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scene = buildRef.current(renderer, readLabPalette(stage), reducedMotion);
    const resize = () => scene.resize(Math.max(240, stage.clientWidth), Math.max(260, stage.clientHeight));
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
    observer?.observe(stage);
    resize();

    let frame = 0;
    let last = performance.now();
    let elapsed = 0;
    const tick = (now: number) => {
      const delta = Math.min(MAX_FRAME_S, (now - last) / 1000);
      last = now;
      if (playingRef.current && !reducedMotion) elapsed += delta;
      scene.update(stateRef.current, elapsed, playingRef.current);
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      scene.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, []);

  return (
    <div
      ref={stageRef}
      className={`lab-scene-stage relative w-full overflow-hidden ${heightClass} rounded-2xl border border-hairline bg-stone2`}
      role="img"
      aria-label={label}
    >
      {unavailable ? (
        <div className="grid h-full place-items-center bg-wash-sky px-6 text-center">
          <div>
            <Rotate3D className="mx-auto h-9 w-9 text-brand-sky" aria-hidden="true" />
            <p className="mt-3 font-extrabold text-ink">3D view unavailable</p>
            <p className="mt-1 text-sm font-medium text-slate2">{label}</p>
          </div>
        </div>
      ) : (
        <p className="pointer-events-none absolute right-3 top-3 rounded-full bg-canvas-pure/90 px-3 py-1.5 text-[11px] font-bold text-slate2 shadow-card-soft">
          {hint}
        </p>
      )}
    </div>
  );
}
